// Token source for shell mode. apiHelpers asks this for the bearer token and, on
// a 401, asks it whether a retry could help. It never calls /login/refresh.
//
// Before badge-in the token is the shell's device token. After verify-pin it is
// the operator token, until the operator clears (lock, inactivity, badge switch)
// and the device token takes over again.

import { readDeviceToken } from './shell';

export interface TokenProvider {
  getToken(): string | null;
  /** Called on a 401 with the token that was sent. true means retry once. */
  handleUnauthorized(sentToken: string | null): boolean;
}

export interface ShellTokenOptions {
  readDevice?: () => string;
  /** The operator token was refused: send the person back to the badge screen. */
  onOperatorExpired?: () => void;
}

export function createShellTokens({ readDevice = readDeviceToken, onOperatorExpired }: ShellTokenOptions = {}) {
  let operatorToken: string | null = null;
  // Operator tokens replaced by a newer one, so a 401 can tell a stale operator
  // request (retry as the current operator) from a pre-badge device request (never
  // retry: that would credit it to whoever badged in since).
  const replaced = new Set<string>();
  let expired = onOperatorExpired;

  return {
    /** The shell app points this at the scanner store, which this module must not import. */
    onOperatorExpired(cb: (() => void) | undefined) {
      expired = cb;
    },
    setOperatorToken(token: string | null | undefined) {
      if (operatorToken) replaced.add(operatorToken);
      operatorToken = token || null;
    },
    clearOperatorToken() {
      operatorToken = null;
      replaced.clear();
    },
    hasOperatorToken: () => operatorToken !== null,
    getToken(): string | null {
      return operatorToken || readDevice() || null;
    },
    handleUnauthorized(sentToken: string | null): boolean {
      if (operatorToken) {
        if (sentToken === operatorToken) {
          operatorToken = null;
          replaced.clear();
          expired?.();
          return false;
        }
        // Stale operator request: retry as the current operator rather than
        // signing out whoever badged in since. A device-token request is not retried.
        return sentToken !== null && replaced.has(sentToken);
      }
      // Device token: the shell refreshes it in the background, so a re-read may
      // have a newer one. The same token again would only 401 again.
      const fresh = readDevice();
      return !!fresh && fresh !== sentToken;
    },
  };
}

export const shellTokens = createShellTokens();
